/**
 * [Phase 2c.6-A.2.2 RL-51] PlayerAnalysisModal - lazy-fetch shimmer modal
 * mounted on player-card click from BOTH `SnakeStandaloneDraftBoard` and
 * `StandaloneDraftBoard` (auction parity).
 *
 * Layout (3 columns at desktop, stacked on mobile):
 *   ┌────────────────────────────────────────────────────────────┐
 *   │  [AdamAvatarBadge]    Adam's take on Patrick Mahomes       │
 *   ├──────────────────┬─────────────────┬───────────────────────┤
 *   │  CONSENSUS       │  ADP / PROJ     │  ROSTER FIT           │
 *   │  (LLM cached)    │  (static)       │  (per-viewer)         │
 *   │                  │                 │  ●●○  Strong fit      │
 *   │                  │                 │  fit_note text…       │
 *   └──────────────────┴─────────────────┴───────────────────────┘
 *
 * Per RL-25: consensus paragraph contains ZERO roster context. Roster-fit
 * is computed independently per-viewer and rendered ADJACENT.
 *
 * Per Q-A2.2-2: endpoint is auth-restricted to commissioner OR drafting
 * team manager. 403 surfaces a friendly "viewers only" empty state.
 *
 * Fallback on 5xx / network error: random line from voice_pools.json's
 * `player_analysis_fallback.lines` (RL-51).
 *
 * Dismiss: Esc / click-outside / Close button.
 */
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { authHeaders, useAuth } from "../../contexts/AuthContext";
import { useEntitlements } from "../../hooks/useEntitlements";
import AdamAvatarBadge from "../AdamAvatarBadge";
import VOICE_POOLS from "../../shared/voice_pools.json";
import { PlayerAnalysisCanvas } from "../analytics/PlayerAnalysisCanvas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// [RL-51 + founder-approved enhancement] Three-dot indicator derived from
// the deterministic roster_fit.fit_score bands. Maps:
//   1.00 → ●●●  Primary need
//   0.75 → ●●○  Strong fit
//   0.45 → ●○○  Depth
//   ≤0.25 → ○○○ Stack
function fitDots(score) {
  if (score >= 0.95) return { dots: "●●●", label: "Primary need" };
  if (score >= 0.65) return { dots: "●●○", label: "Strong fit" };
  if (score >= 0.35) return { dots: "●○○", label: "Depth" };
  return { dots: "○○○", label: "Stack" };
}

function fallbackLine(player) {
  const lines = VOICE_POOLS?.player_analysis_fallback?.lines || [];
  if (!lines.length) {
    return `Take on ${player?.name || "this player"} is offline. Use ADP and roster fit.`;
  }
  const tpl = lines[Math.floor(Math.random() * lines.length)];
  return tpl
    .replace("{name}", player?.name || "this player")
    .replace("{position}", player?.position || "?")
    .replace("{nfl_team}", player?.nfl_team || "FA");
}

export default function PlayerAnalysisModal({
  leagueId,
  player,           // { id, name, position, nfl_team }  - required for shimmer header
  onClose,
  isDark = true,
  draftEpoch = 0,   // [D.3] increments on PICK_MADE / SALE_COMPLETE - triggers canvas re-fetch for live gold/silver derivation
  showPgfValue = true, // snake callers pass false - no dollars in a snake draft
}) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // [TD-47.6c-D.1 + Phase 2d Session B] PGF analytics surface - gated by
  // `pgf_analytics_alpha` (legacy) OR `has_draft_analytics` toggle.
  const { user } = useAuth();
  const { hasDraftAnalytics } = useEntitlements();
  const analyticsEnabled =
    user?.role === "admin" || !!user?.alpha_flags?.pgf_analytics_alpha || hasDraftAnalytics;
  const [pgfCanvas, setPgfCanvas] = useState(null);

  // Lazy POST on mount.
  useEffect(() => {
    let cancelled = false;
    if (!leagueId || !player?.id) return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await axios.post(
          `${API}/draft/${leagueId}/player-analysis`,
          { player_id: player.id },
          { headers: authHeaders() }
        );
        if (cancelled) return;
        setData(r.data);
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 403) {
          setError({
            kind: "auth",
            text: "Player analysis is for commissioner and drafting managers only.",
          });
        } else {
          // 5xx / network → render fallback paragraph from voice_pools.
          setError({ kind: "fallback", text: fallbackLine(player) });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [leagueId, player?.id, player?.name, player?.position, player?.nfl_team]);

  // [D.1] Lazy-fetch the PGF analytics canvas alongside the existing LLM
  // analysis. Independent endpoint - 404s and 422s (unscored positions
  // like K/DEF) gracefully degrade to no-render; existing surface unaffected.
  useEffect(() => {
    let cancelled = false;
    if (!analyticsEnabled || !player?.id) return;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/analytics/players/${player.id}/canvas`,
          {
            params: { season_year: 2024, scoring_format: "half_ppr", league_id: leagueId },
            headers: authHeaders(),
          }
        );
        if (!cancelled) setPgfCanvas(r.data);
      } catch {
        if (!cancelled) setPgfCanvas(null);
      }
    })();
    return () => { cancelled = true; };
  }, [analyticsEnabled, player?.id, leagueId, draftEpoch]);

  // Dismiss handlers.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const overlayBg = isDark ? "rgba(0,0,0,0.78)" : "rgba(240,240,240,0.88)";
  const cardBg = isDark ? "#141414" : "#FFFFFF";
  const cardBorder = isDark ? "#2E2E2E" : "#D8D8D8";
  const titleColor = isDark ? "#F5F5F5" : "#0A0A0A";
  const subColor = isDark ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.62)";
  const accent = "#FF6B00";
  const dotsActive = isDark ? "#F5F5F5" : "#0A0A0A";

  const fit = data?.roster_fit;
  const dots = useMemo(
    () => (fit ? fitDots(fit.fit_score) : { dots: "○○○", label: "-" }),
    [fit]
  );

  return (
    <div
      data-testid="player-analysis-modal-overlay"
      className="fixed inset-0 z-[65] flex items-center justify-center p-4"
      style={{ background: overlayBg, backdropFilter: "blur(8px)" }}
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="rounded-2xl w-full max-w-5xl p-6 shadow-2xl"
        style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
        data-testid="player-analysis-modal-card"
      >
        {/* ── Header: badge + player name + close ──────────────────────── */}
        <div className="flex items-start gap-3 mb-4">
          <div className="shrink-0 mt-1">
            <AdamAvatarBadge size={36} isDark={isDark} />
          </div>
          <div className="flex-1 min-w-0">
            <div
              className="font-display text-[10px] uppercase tracking-[0.22em]"
              style={{ color: accent }}
            >
              Adam's scouting take
            </div>
            <div
              className="font-display font-black text-xl leading-tight truncate"
              style={{ color: titleColor }}
              data-testid="player-analysis-modal-title"
            >
              {player?.name || "Player"}
              <span
                className="font-display font-bold text-sm ml-2"
                style={{ color: subColor }}
              >
                {player?.position} · {player?.nfl_team || "FA"}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="player-analysis-modal-close"
            className="w-8 h-8 rounded-lg flex items-center justify-center transition-all"
            style={{
              color: subColor,
              border: `1px solid ${cardBorder}`,
              background: "transparent",
            }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* [TD-47.6c-D.2] Canvas v2 - mounted when alpha flag is on AND
            the analytics endpoint returned data. Legacy 3-column layout
            below remains the fallback (preserves roster-fit view for
            non-alpha users + when canvas data is absent / degraded). */}
        {analyticsEnabled && pgfCanvas ? (
          <PlayerAnalysisCanvas
            player={player}
            pgfCanvas={pgfCanvas}
            analysisData={data}
            loading={loading}
            error={error}
            isDark={isDark}
            showPgfValue={showPgfValue}
          />
        ) : (
        <>
        {/* ── 3-column grid (legacy / fallback) ────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {/* Consensus paragraph - 7 cols on md+ */}
          <div className="md:col-span-7">
            <div
              className="font-display text-[10px] uppercase tracking-[0.22em] mb-2"
              style={{ color: subColor }}
            >
              Consensus
            </div>
            {loading ? (
              <PlayerAnalysisShimmer isDark={isDark} />
            ) : error ? (
              <p
                className="font-body text-sm leading-relaxed"
                style={{ color: titleColor }}
                data-testid={
                  error.kind === "auth"
                    ? "player-analysis-auth-msg"
                    : "player-analysis-fallback"
                }
              >
                {error.text}
              </p>
            ) : (
              <p
                className="font-body text-sm leading-relaxed"
                style={{ color: titleColor }}
                data-testid="player-analysis-consensus"
              >
                {data?.consensus}
              </p>
            )}
          </div>

          {/* ADP / Projected - 2 cols */}
          <div className="md:col-span-2">
            <div
              className="font-display text-[10px] uppercase tracking-[0.22em] mb-2"
              style={{ color: subColor }}
            >
              ADP / Proj
            </div>
            {loading ? (
              <ShimmerBlock height={64} isDark={isDark} />
            ) : (
              <div
                className="rounded-lg px-3 py-2 text-center"
                style={{
                  background: isDark ? "#0F0F12" : "#F4F4F4",
                  border: `1px solid ${cardBorder}`,
                }}
                data-testid="player-analysis-adp-block"
              >
                <div
                  className="font-display font-black text-lg leading-tight"
                  style={{ color: titleColor }}
                >
                  {data?.adp != null ? data.adp.toFixed(1) : "-"}
                </div>
                <div className="font-body text-[10px]" style={{ color: subColor }}>
                  ADP
                </div>
                <div className="my-1.5" style={{ borderTop: `1px solid ${cardBorder}` }} />
                <div
                  className="font-display font-bold text-sm leading-tight"
                  style={{ color: titleColor }}
                >
                  {data?.projected_points != null
                    ? `${data.projected_points.toFixed(0)} pts`
                    : "-"}
                </div>
                <div className="font-body text-[10px]" style={{ color: subColor }}>
                  Projected
                </div>
              </div>
            )}
          </div>

          {/* Roster fit - 3 cols */}
          <div className="md:col-span-3">
            <div
              className="font-display text-[10px] uppercase tracking-[0.22em] mb-2"
              style={{ color: subColor }}
            >
              Your roster fit
            </div>
            {loading ? (
              <ShimmerBlock height={64} isDark={isDark} />
            ) : !fit ? (
              <p className="font-body text-xs" style={{ color: subColor }}>
                Not available.
              </p>
            ) : (
              <div
                className="rounded-lg px-3 py-2"
                style={{
                  background: isDark ? "#0F0F12" : "#F4F4F4",
                  border: `1px solid ${cardBorder}`,
                }}
                data-testid="player-analysis-fit-block"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <span
                    className="font-display font-black text-base tracking-widest"
                    style={{ color: dotsActive }}
                    data-testid="player-analysis-fit-dots"
                    aria-label={`Fit score ${dots.label}`}
                  >
                    {dots.dots}
                  </span>
                  <span
                    className="font-display font-bold text-[10px] uppercase tracking-wider"
                    style={{ color: accent }}
                  >
                    {dots.label}
                  </span>
                </div>
                <p
                  className="font-body text-xs leading-snug"
                  style={{ color: titleColor }}
                  data-testid="player-analysis-fit-note"
                >
                  {fit.fit_note}
                </p>
                {Array.isArray(fit.eligible_slots) && fit.eligible_slots.length > 0 && (
                  <div
                    className="mt-1.5 font-body text-[10px]"
                    style={{ color: subColor }}
                  >
                    Slots: {fit.eligible_slots.join(" · ")}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
}

function ShimmerBlock({ height = 60, isDark = true }) {
  const base = isDark ? "#1C1C1C" : "#EEEEEE";
  const sheen = isDark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.45)";
  return (
    <div
      className="rounded-lg overflow-hidden relative"
      style={{ height, background: base }}
      data-testid="player-analysis-shimmer-block"
    >
      <div
        className="absolute inset-y-0 -left-1/2 w-1/2"
        style={{
          background: `linear-gradient(90deg, transparent, ${sheen}, transparent)`,
          animation: "pgf-pa-shimmer 1.4s ease-in-out infinite",
        }}
      />
      <style>{`
        @keyframes pgf-pa-shimmer {
          0%   { transform: translateX(0); }
          100% { transform: translateX(400%); }
        }
      `}</style>
    </div>
  );
}

function PlayerAnalysisShimmer({ isDark }) {
  return (
    <div className="space-y-2" data-testid="player-analysis-shimmer">
      <ShimmerBlock height={14} isDark={isDark} />
      <ShimmerBlock height={14} isDark={isDark} />
      <ShimmerBlock height={14} isDark={isDark} />
      <div
        className="font-display text-[10px] uppercase tracking-widest mt-1"
        style={{ color: isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)" }}
      >
        Adam's scouting…
      </div>
    </div>
  );
}
